import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  CompactSelection,
  GridCellKind,
  type EditableGridCell,
  type GridCell,
  type GridSelection,
} from '@glideapps/glide-data-grid';
import type { FundGridPage, FundListItemView, FundSummary } from '@nemo/api';
import { ListPlus, Plus, Search, SlidersHorizontal, Radar } from 'lucide-react';
import { DensityToggle } from './grid/DensityToggle.js';
import {
  REGIONS,
  SECTOR_TAGS,
  chequeDisplay,
  isFundSortColumn,
  toCsv,
  type ListStatus,
} from '@nemo/core/model';
import { useSearchParams } from 'wouter';
import { useAffinity } from './affinity/useAffinity.js';
import { ALL_INVESTOR_TYPES, countryName, investorTypeLabel } from './dealroom/format.js';
import { useDealroom } from './dealroom/useDealroom.js';
import { EnrichmentDrawer } from './enrichment/EnrichmentDrawer.js';
import { useEnrichment } from './enrichment/useEnrichment.js';
import { FundPane } from './pane/FundPane.js';
import { useFundLists } from './pane/FundPaneHost.js';
import { orderedColumns, type Sort } from './grid/columns.js';
import { DataGrid } from './grid/DataGrid.js';
import { paletteValue } from './palette.js';
import { download, today } from './grid/csv.js';
import {
  FUND_COLUMNS,
  coInvestmentLines,
  recentInvestmentTipLines,
  exportColumnsOf,
  regionsTitle,
  stageTitle,
  unclaimedSectors,
  whoKnowsPairs,
  type FundColumnDef,
  type FundGridContext,
} from './grid/fundColumns.js';
import { FilterDialog, type FilterOptions } from './grid/FilterDialog.js';
import { ActiveFilters } from './ui/FilterDialog.js';
import {
  EUROPE_FILTER,
  NO_FILTERS,
  filterChips,
  gridQueryOf,
  type FundGridFilters,
} from './grid/fundFilters.js';
import type { FundWrite } from './grid/fundWrites.js';
import { useFundWriter } from './grid/useFundWriter.js';
import { useGridPreferences } from './grid/useGridPreferences.js';
import { AddFundsSearch } from './lists/AddFundsSearch.js';
import { AddTargetBar } from './lists/AddTargetBar.js';
import { AddToListModal } from './lists/AddToListModal.js';
import { listGridColumns, listKeysOf } from './lists/listGridColumns.js';
import { useFundList } from './lists/useFundList.js';
import { useUndoStack } from './grid/useUndoStack.js';
import { markedCells } from './research/status.js';
import { useResearch } from './research/useResearch.js';
import { ExportButton } from './sheets/ExportButton.js';
import { useTRPC, useTRPCClient } from './trpc.js';
import type { MenuItem } from './ui/Menu.js';
import { useToast } from './ui/Toast.js';
import { invalidateFundRows } from './grid/invalidateFundRows.js';

/**
 * Which half of the funds a grid is showing: the tracked funds Project A works
 * from, or the firms it has only come across (not tracked). Stored on the fund
 * (`listStatus`: `list` is tracked, `pool` is not): a person tracks or stops
 * tracking a fund, and a tier, an owner, a fund list or a relationship arriving
 * tracks it by itself.
 */
export type FundScope = ListStatus | { readonly listId: string };

/**
 * Bumped when the column set changed: an order stored against the old set
 * would pin the old arrangement and bring back columns that no longer exist.
 */
const PREFERENCES_KEY = 'nemo.grid.funds.v3';

/** How many pairings a hover lists before it starts counting instead. */
const TIP_PAIRS_SHOWN = 12;

/** Three ways in, said once, where the third one is not discoverable at all. */
const OPEN_TIP = 'Open this fund — click here, click its name, or press Enter on the row.';

/** The cells a click opens the fund from; the rest are for editing. */
const OPENS_FROM: ReadonlySet<string> = new Set([
  'name',
  'whoKnows',
  'recentInvestments',
  'coInvested',
]);

const SEARCH_DEBOUNCE_MS = 300;

/** A page is 50 rows and a screen about 25: ask late enough that one screen costs one page. */
const LOAD_AHEAD_ROWS = 15;

/** How many funds a CSV asks for at a time. */
const EXPORT_PAGE_SIZE = 500;

const NOTHING_SELECTED: GridSelection = {
  columns: CompactSelection.empty(),
  rows: CompactSelection.empty(),
};

/**
 * The rows the pages hold. A fund edited out of its place can come round again
 * on a later page, and one somebody stopped tracking is gone at once.
 */
function rowsOfPages(
  data: InfiniteData<FundGridPage, unknown> | undefined,
  status: ListStatus | null,
): FundSummary[] {
  const seen = new Set<string>();
  return (data?.pages ?? [])
    .flatMap((page) => page.funds)
    .filter((fund) => {
      if (fund.listStatus !== status || seen.has(fund.id)) return false;
      seen.add(fund.id);
      return true;
    });
}

export interface FundsGridProps {
  scope: FundScope;
}

/** What the Add to list dialog is about to add, and how it was asked for. */
interface Adding {
  fundIds: string[];
  title: string;
  suggestedName?: string;
  newOnly?: boolean;
}

/**
 * The funds table: the same grid, the same toolbar and the same filters over
 * either half of the list. It renders inside a `.page`, never as one, so the
 * candidates page can put it under a row of tabs.
 */
export function FundsGrid(props: FundsGridProps): React.JSX.Element {
  const { scope } = props;
  const listId = typeof scope === 'object' ? scope.listId : null;
  const status: ListStatus | null = typeof scope === 'object' ? null : scope;
  const fundList = useFundList(listId);
  const list = fundList.list;
  // A deleted list stays readable and exportable; changing it means restoring it first.
  const locked = list?.deletedAt != null;
  const [adding, setAdding] = useState<Adding | null>(null);
  const trpc = useTRPC();
  const toast = useToast();
  const client = useTRPCClient();
  const queryClient = useQueryClient();
  const sectors = useQuery(trpc.sectors.list.queryOptions());

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput]);
  const [filters, setFilters] = useState<FundGridFilters>(NO_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sort, setSort] = useState<Sort | null>(null);
  // Which fund the panel is showing, and the section it was opened at.
  // A person is focused when the pane was opened from a link to them.
  const [open, setOpen] = useState<{
    fundId: string;
    at: 'investments' | null;
    person: string | null;
  } | null>(null);
  // The Enrichment drawer occupies the same slot as the fund panel; opening
  // one replaces the other rather than stacking.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selection, setSelection] = useState<GridSelection>(NOTHING_SELECTED);
  // "Select all" reaches past the rows loaded so far; which those are is the server's to say.
  const [allMatching, setAllMatching] = useState(false);
  const preferences = useGridPreferences(PREFERENCES_KEY);
  const undoStack = useUndoStack();
  const twoLine = preferences.density === 'comfortable';

  const lists = useFundLists();

  const research = useResearch();
  const affinity = useAffinity();
  const dealroom = useDealroom();
  const enrichment = useEnrichment(drawerOpen);

  // A round names the funds that were in it, and those names link back here.
  const [searchParams] = useSearchParams();
  // A partner named on a round links here too, at the person rather than the
  // rounds.
  const linkedFundId = searchParams.get('fund');
  const linkedPersonId = searchParams.get('person');
  useEffect(() => {
    if (linkedFundId === null) return;
    setDrawerOpen(false);
    setOpen(
      linkedPersonId === null
        ? { fundId: linkedFundId, at: 'investments', person: null }
        : { fundId: linkedFundId, at: null, person: linkedPersonId },
    );
  }, [linkedFundId, linkedPersonId]);

  const listItems: ReadonlyMap<string, FundListItemView> | undefined = useMemo(
    () => (list === undefined ? undefined : new Map(list.items.map((item) => [item.fundId, item]))),
    [list],
  );

  const context: FundGridContext = useMemo(
    () => ({ ...lists, research: research.byFund, twoLine, listItems }),
    [lists, research.byFund, twoLine, listItems],
  );

  const writer = useFundWriter(lists);

  const columns: readonly FundColumnDef[] = useMemo(() => {
    // A list shows its own column set in its own order; dragging a header
    // there reorders the list, not this person's grid.
    if (listId !== null) return list === undefined ? [] : listGridColumns(list);
    return orderedColumns(FUND_COLUMNS, preferences);
  }, [preferences, listId, list]);

  const query = useMemo(
    () => gridQueryOf(status ?? 'list', search, filters, sort),
    [status, search, filters, sort],
  );

  const page = useInfiniteQuery(
    trpc.funds.page.infiniteQueryOptions(query, {
      enabled: status !== null,
      getNextPageParam: (last) => last.nextCursor,
      // The rows stay up while a new search or sort is on its way.
      placeholderData: keepPreviousData,
    }),
  );

  // A selection is positions in this order, which means nothing in another.
  useEffect(() => {
    setSelection(NOTHING_SELECTED);
    setAllMatching(false);
  }, [query]);

  const rows: readonly FundSummary[] = useMemo(() => {
    if (listId === null) return rowsOfPages(page.data, status);
    return (list?.items ?? []).map((item) => item.fund);
  }, [page.data, status, listId, list]);

  /** How many funds these filters keep, loaded or not. */
  const matching = page.data?.pages[0]?.matching ?? rows.length;

  useEffect(() => {
    if (allMatching) {
      setSelection((current) => ({
        ...current,
        rows: CompactSelection.fromSingleSelection([0, rows.length]),
      }));
    }
  }, [allMatching, rows.length]);

  const matchingIds = useQuery(
    trpc.funds.matchingIds.queryOptions(query, {
      enabled: status !== null && (allMatching || drawerOpen),
    }),
  );

  const filterChoices = useQuery(
    trpc.funds.filterOptions.queryOptions(
      { listStatus: status ?? 'list' },
      { enabled: status !== null && filtersOpen },
    ),
  );

  const filterOptions: FilterOptions = useMemo(() => {
    const choices = filterChoices.data;
    // The saved sector list plus any tag only the rounds have shown, so a
    // fund's hollow chip is something the filter can pick. Taxonomy order
    // first, legacy values after.
    const names = new Set((sectors.data ?? []).map((sector) => sector.name));
    for (const tag of choices?.observedSectors ?? []) names.add(tag);
    const taxonomy: readonly string[] = SECTOR_TAGS;
    const present = new Set<string>(choices?.regions ?? []);
    return {
      members: [...(choices?.members ?? [])]
        .sort((left, right) => left.localeCompare(right))
        .map((name) => ({ value: name, label: name })),
      types: ALL_INVESTOR_TYPES.filter((type) => (choices?.types ?? []).includes(type)).map(
        (type) => ({ value: type, label: investorTypeLabel(type) }),
      ),
      sectors: [
        ...taxonomy.filter((tag) => names.has(tag)),
        ...[...names].filter((name) => !taxonomy.includes(name)),
      ].map((name) => ({ value: name, label: name })),
      regions: [
        { value: EUROPE_FILTER, label: 'Europe (any)' },
        ...REGIONS.filter((region) => present.has(region)).map((region) => ({
          value: region,
          label: region,
        })),
      ],
      hq: (choices?.hq ?? [])
        .map((code) => ({ value: code, label: countryName(code) }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    };
  }, [filterChoices.data, sectors.data]);

  const activeFilters = useMemo(() => filterChips(filters), [filters]);

  const showFund = useCallback((fundId: string, at: 'investments' | null): void => {
    setDrawerOpen(false);
    setOpen({ fundId, at, person: null });
  }, []);

  const decorate = useCallback(
    (fund: FundSummary, column: FundColumnDef, cell: GridCell): GridCell =>
      markedCells(research.byFund.get(fund.id)).has(column.id) ? markProposed(cell) : cell,
    [research.byFund],
  );

  const write = useCallback(
    async (fund: FundSummary, column: FundColumnDef, next: FundWrite): Promise<void> => {
      const before = column.current?.(fund, context);
      const fundId = fund.id;
      try {
        await writer.apply(fundId, next);
      } catch {
        return;
      }
      if (before === undefined) return;
      undoStack.push({
        undo: () => writer.apply(fundId, before),
        redo: () => writer.apply(fundId, next),
      });
    },
    [writer, undoStack, context],
  );

  const onCellEdited = useCallback(
    (fund: FundSummary, column: FundColumnDef, value: EditableGridCell): void => {
      if (locked) return;
      const item = listItems?.get(fund.id);
      if (column.itemEdit !== undefined && item !== undefined) {
        const edit = column.itemEdit(value);
        if (edit === null) toast(`That is not a value ${column.title} can hold.`);
        else fundList.editItem(item.id, edit);
        return;
      }
      if (column.commit === undefined) return;
      const next = column.commit(fund, value);
      if (next === null) {
        toast(`That is not a value ${column.title} can hold.`);
        return;
      }
      void write(fund, column, next);
    },
    [toast, write, listItems, fundList, locked],
  );

  /** The rows up to `count`, loading the pages between here and there. */
  const rowsThrough = useCallback(
    async (count: number): Promise<readonly FundSummary[]> => {
      if (listId !== null) return rows;
      let current = rows;
      let more = page.hasNextPage;
      while (current.length < count && more) {
        const next = await page.fetchNextPage();
        current = rowsOfPages(next.data, status);
        more = next.hasNextPage;
      }
      return current;
    },
    [listId, rows, page, status],
  );

  /** Every fund the filters keep, in order, for a file: a list has them all already. */
  const everyMatchingRow = useCallback(async (): Promise<readonly FundSummary[]> => {
    if (listId !== null) return rows;
    const all: FundSummary[] = [];
    let cursor: string | undefined;
    do {
      const next = await client.funds.page.query({
        ...query,
        limit: EXPORT_PAGE_SIZE,
        ...(cursor === undefined ? {} : { cursor }),
      });
      all.push(...next.funds);
      cursor = next.nextCursor ?? undefined;
    } while (cursor !== undefined);
    return all;
  }, [listId, rows, client, query]);

  /** The ids of every fund the filters keep, for what is sent as ids rather than shown. */
  const everyMatchingId = useCallback(async (): Promise<readonly string[]> => {
    if (listId !== null) return rows.map((fund) => fund.id);
    return queryClient.fetchQuery(trpc.funds.matchingIds.queryOptions(query));
  }, [listId, rows, queryClient, trpc, query]);

  const onPaste = useCallback(
    async (
      start: { column: number; row: number },
      values: readonly (readonly string[])[],
    ): Promise<void> => {
      if (locked) return;
      const writes: { fundId: string; write: FundWrite; before: FundWrite }[] = [];

      // A block can run past the rows loaded so far, and every row of it must land.
      const rows = await rowsThrough(start.row + values.length);

      for (const [rowOffset, line] of values.entries()) {
        for (const [colOffset, cellText] of line.entries()) {
          const column = columns[start.column + colOffset];
          const fund = rows[start.row + rowOffset];
          if (column === undefined || fund === undefined) continue;
          if (column.paste === undefined) {
            toast(`${column.title} cannot be pasted into; edit it in the cell instead.`);
            return;
          }
          const parsed = column.paste(fund, cellText);
          if (!parsed.ok) {
            toast(`${fund.name}, ${column.title}: ${parsed.reason}`);
            return;
          }
          const before = column.current?.(fund, context);
          if (before === undefined) continue;
          writes.push({ fundId: fund.id, write: parsed.value, before });
        }
      }

      if (writes.length === 0) return;
      const next = mergeWrites(
        writes.map((entry) => ({ fundId: entry.fundId, write: entry.write })),
      );
      const previous = mergeWrites(
        writes.map((entry) => ({ fundId: entry.fundId, write: entry.before })),
      );

      void writer
        .applyMany(next)
        .then(() => {
          undoStack.push({
            undo: () => writer.applyMany(previous),
            redo: () => writer.applyMany(next),
          });
        })
        .catch(() => undefined);
    },
    [columns, rowsThrough, context, toast, writer, undoStack, locked],
  );

  const exportCsv = useCallback(async (): Promise<void> => {
    const exported = exportColumnsOf(columns, context);
    const content = toCsv(
      exported.map((column) => column.title),
      (await everyMatchingRow()).map((fund) => exported.map((column) => column.text(fund))),
    );
    const what = status === 'list' ? 'follow-on-investors' : 'follow-on-investors-not-tracked';
    download(`${what}-${today()}.csv`, content);
  }, [columns, everyMatchingRow, context, status]);

  const selectedFundIds = useMemo(() => {
    if (allMatching && matchingIds.data !== undefined) return matchingIds.data;
    return selection.rows.toArray().flatMap((index) => {
      const fund = rows[index];
      return fund === undefined ? [] : [fund.id];
    });
  }, [rows, selection, allMatching, matchingIds.data]);

  /**
   * A person who selected rows meant those rows; a person who filtered and
   * selected nothing meant everything the filters keep, loaded or not.
   */
  const researchTargets = useMemo(() => {
    if (selectedFundIds.length > 0) {
      return { ids: selectedFundIds, label: `selected (${selectedFundIds.length})` };
    }
    const ids = listId !== null ? rows.map((fund) => fund.id) : (matchingIds.data ?? []);
    return { ids: [...ids], label: `filtered (${listId !== null ? rows.length : matching})` };
  }, [selectedFundIds, listId, rows, matchingIds.data, matching]);

  /** The rows the right-click was on: the selection if it holds that row, else just it. */
  const menuFundIds = (fundId: string): string[] =>
    selectedFundIds.includes(fundId) ? selectedFundIds : [fundId];

  const moveItem = (fundId: string, delta: 1 | -1): void => {
    const item = listItems?.get(fundId);
    if (item === undefined) return;
    fundList.moveItem(item.id, item.position + delta);
  };

  /**
   * Track or stop tracking, for the row the menu is on and the
   * rest of the selection with it; one undo step for the lot.
   */
  const moveMenuItem = (fundId: string): MenuItem => {
    const current = rows.find((fund) => fund.id === fundId)?.listStatus ?? 'pool';
    const to: ListStatus = current === 'list' ? 'pool' : 'list';
    const moving = rows.filter(
      (fund) => menuFundIds(fundId).includes(fund.id) && fund.listStatus !== to,
    );
    const writes = (value: (fund: FundSummary) => ListStatus) =>
      moving.map((fund): { fundId: string; write: FundWrite } => ({
        fundId: fund.id,
        write: { kind: 'edits', edits: [{ column: 'listStatus', value: value(fund) }] },
      }));
    const label = to === 'list' ? 'Track fund' : 'Stop tracking';
    return {
      label: moving.length > 1 ? `${label} (${moving.length})` : label,
      onSelect: () => {
        const next = writes(() => to);
        const before = writes((fund) => fund.listStatus);
        void writer
          .applyMany(next)
          .then(() => {
            undoStack.push({
              undo: () => writer.applyMany(before),
              redo: () => writer.applyMany(next),
            });
            // The counts, and the rows the other half now holds.
            void queryClient.invalidateQueries({ queryKey: trpc.funds.page.pathKey() });
            void queryClient.invalidateQueries({ queryKey: trpc.funds.count.pathKey() });
          })
          .catch(() => undefined);
      },
    };
  };

  /** The row menu: the selection if it holds this row, else just it. */
  const rowMenuItems = (fund: FundSummary): MenuItem[] => [
    { label: 'Open fund', onSelect: () => showFund(fund.id, null) },
    ...(listId === null
      ? [
          {
            label: 'Add to list…',
            onSelect: () => {
              const fundIds = menuFundIds(fund.id);
              setAdding({
                fundIds,
                title: `Add ${fundIds.length} fund${fundIds.length === 1 ? '' : 's'} to a list`,
              });
            },
          },
        ]
      : [
          {
            label: 'Move up',
            disabled: locked || listItems?.get(fund.id)?.position === 0,
            onSelect: () => moveItem(fund.id, -1),
          },
          {
            label: 'Move down',
            disabled: locked || listItems?.get(fund.id)?.position === rows.length - 1,
            onSelect: () => moveItem(fund.id, 1),
          },
          {
            label: 'Remove from list',
            danger: true,
            disabled: locked,
            onSelect: () => {
              const item = listItems?.get(fund.id);
              if (item !== undefined) fundList.removeItem(item.id);
            },
          },
        ]),
    moveMenuItem(fund.id),
    {
      label: 'Copy fund name',
      onSelect: () => {
        void navigator.clipboard
          .writeText(fund.name)
          .then(() => toast(`Copied “${fund.name}”.`, { tone: 'info' }))
          .catch(() => toast('Could not reach the clipboard.'));
      },
    },
    {
      label: 'Research this fund',
      disabled: research.isStarting,
      onSelect: () => research.start([fund.id]),
    },
  ];

  const createFund = useCreateFund();

  // A link can name a fund the rows have not reached, or that these filters leave out.
  const openInRows = open === null ? undefined : rows.find((fund) => fund.id === open.fundId);
  const openAlone = useQuery(
    trpc.funds.get.queryOptions(
      { id: open?.fundId ?? '' },
      { enabled: open !== null && openInRows === undefined },
    ),
  );
  const openFund = open === null ? null : (openInRows ?? openAlone.data);

  // A list's rows arrive with the list; only the two halves wait on a page.
  if ((listId === null && page.isPending) || fundList.isPending) {
    return <p className="subtle">Loading the funds…</p>;
  }
  if (listId === null && page.isError)
    return <p className="subtle">Could not load the funds: {page.error.message}</p>;
  if (fundList.error !== null)
    return <p className="subtle">Could not load the funds: {fundList.error}</p>;

  return (
    <>
      {listId !== null ? (
        <div className="toolbar">
          <strong className="count">
            {rows.length} fund{rows.length === 1 ? '' : 's'}
          </strong>
          <span className="subtle">
            Drag a row by its number to reorder, or right-click it to move or remove it.
          </span>
          {list !== undefined && !locked && <AddFundsSearch list={list} />}
          <span className="spacer" />
          <DensityToggle density={preferences.density} onChange={preferences.setDensity} />
          <button
            className="button"
            type="button"
            disabled={!undoStack.canUndo}
            onClick={undoStack.undo}
          >
            Undo
          </button>
          <button
            className="button"
            type="button"
            disabled={!undoStack.canRedo}
            onClick={undoStack.redo}
          >
            Redo
          </button>
        </div>
      ) : (
        <>
          <div className="toolbar">
            <strong className="count">
              {matching} {status === 'list' ? 'tracked funds' : 'funds'}
            </strong>
            <span className="field-with-icon">
              <Search aria-hidden="true" />
              <input
                className="search"
                placeholder="Search…"
                aria-label="Search the funds"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
              />
            </span>
            <button
              className="button"
              type="button"
              aria-haspopup="dialog"
              aria-expanded={filtersOpen}
              onClick={() => setFiltersOpen(true)}
            >
              <SlidersHorizontal aria-hidden="true" />
              Filters
              {activeFilters.length > 0 && (
                <span className="filter-count">{activeFilters.length}</span>
              )}
            </button>
            {/* The checkbox in the header ticks what is loaded; this is the rest. */}
            {page.hasNextPage && selection.rows.length === rows.length && rows.length > 0 && (
              <button
                className="button"
                type="button"
                disabled={allMatching}
                onClick={() => setAllMatching(true)}
              >
                {allMatching ? `All ${matching} selected` : `Select all ${matching}`}
              </button>
            )}
            {/* Only while rows are selected: what to do with them. */}
            {selectedFundIds.length > 0 && (
              <button
                className="button"
                type="button"
                onClick={() =>
                  setAdding({
                    fundIds: selectedFundIds,
                    title: `Add ${selectedFundIds.length} fund${selectedFundIds.length === 1 ? '' : 's'} to a list`,
                  })
                }
              >
                <ListPlus aria-hidden="true" />
                Add to list…
              </button>
            )}
            <AddTargetBar fundIds={selectedFundIds} />
            <span className="spacer" />
            <DensityToggle density={preferences.density} onChange={preferences.setDensity} />
            <button
              className="button"
              type="button"
              onClick={() => {
                const name = window.prompt('New fund name');
                if (name !== null && name.trim() !== '') createFund(name.trim());
              }}
            >
              <Plus aria-hidden="true" />
              Add fund
            </button>
            <ExportButton
              downloadCsv={() => {
                exportCsv().catch((error: unknown) => {
                  toast(error instanceof Error ? error.message : 'Could not read the funds.');
                });
              }}
              scope={async () => ({
                grid: {
                  fundIds: [...(await everyMatchingId())],
                  columns: exportColumnsOf(columns, context).map((column) => ({
                    key: column.key,
                    header: column.title,
                  })),
                },
              })}
            />
            <button
              className="button enrichment-button"
              type="button"
              onClick={() => {
                setOpen(null);
                setDrawerOpen(true);
              }}
            >
              <Radar aria-hidden="true" />
              Enrichment
              {enrichment.status?.running === true && <span className="dot" aria-hidden="true" />}
            </button>
          </div>
          <ActiveFilters
            chips={activeFilters}
            onChange={setFilters}
            onClearAll={() => setFilters(NO_FILTERS)}
          >
            <span className="spacer" />
            {matching > 0 && (
              <button
                className="button small"
                type="button"
                onClick={() => {
                  everyMatchingId()
                    .then((fundIds) =>
                      setAdding({
                        fundIds: [...fundIds],
                        title: `Save ${fundIds.length} rows as a list`,
                        newOnly: true,
                      }),
                    )
                    .catch((error: unknown) => {
                      toast(error instanceof Error ? error.message : 'Could not read the funds.');
                    });
                }}
              >
                Save {matching} rows as a list
              </button>
            )}
          </ActiveFilters>
          {filtersOpen && (
            <FilterDialog
              filters={filters}
              options={filterOptions}
              matching={matching}
              onChange={setFilters}
              onClose={() => setFiltersOpen(false)}
            />
          )}
        </>
      )}

      <DataGrid
        rows={rows}
        columns={columns}
        context={context}
        rowId={(fund) => fund.id}
        preferences={preferences}
        // A list is read in its own order, not sorted out of it.
        sort={
          listId === null
            ? { value: sort, onChange: setSort, sortable: isFundSortColumn }
            : undefined
        }
        paging={
          listId === null
            ? {
                hasMore: page.hasNextPage,
                loading: page.isFetchingNextPage,
                onReachEnd: () => void page.fetchNextPage(),
                loadAhead: LOAD_AHEAD_ROWS,
              }
            : undefined
        }
        onColumnMoved={
          locked
            ? null
            : listId === null
              ? undefined
              : (from, to) => {
                  // A list shows its own column set in its own order; dragging
                  // a header there reorders the list, not this person's grid.
                  const reordered = [...columns];
                  const [moved] = reordered.splice(from, 1);
                  if (moved === undefined) return;
                  reordered.splice(to, 0, moved);
                  fundList.update({ columns: listKeysOf(reordered) });
                }
        }
        scrollResetKey={listId === null ? query : undefined}
        siteIcon={{ columnId: 'name', of: (fund) => fund }}
        open={{
          openId: open?.fundId ?? null,
          onOpen: (fund, columnId) =>
            showFund(
              fund.id,
              columnId === 'recentInvestments' || columnId === 'coInvested' ? 'investments' : null,
            ),
          onClose: () => setOpen(null),
          opensFrom: (columnId) => OPENS_FROM.has(columnId),
          chevronTip: OPEN_TIP,
        }}
        tip={tipFor}
        decorate={decorate}
        rowMenu={{ label: 'Fund', items: rowMenuItems }}
        onCellEdited={onCellEdited}
        onPaste={(start, values) => void onPaste(start, values)}
        selection={{
          value: selection,
          onChange: (next) => {
            setAllMatching(false);
            setSelection(next);
          },
        }}
        multiSelect
        onRowMoved={
          listId === null || locked
            ? undefined
            : (from, to) => {
                const ids = (list?.items ?? []).map((item) => item.id);
                const [moved] = ids.splice(from, 1);
                if (moved === undefined) return;
                ids.splice(to, 0, moved);
                fundList.reorder(ids);
              }
        }
        // A list is read across, so its fund names stay in view as it scrolls.
        freezeColumns={listId !== null && columns[0]?.id === 'name' ? 1 : 0}
        aside={(step) =>
          !drawerOpen &&
          openFund !== undefined &&
          openFund !== null && (
            <FundPane
              fund={openFund}
              lists={lists}
              research={research.byFund.get(openFund.id)}
              estimate={research.estimate}
              openAt={open?.at ?? null}
              focusPerson={open?.person ?? null}
              writer={writer}
              undoStack={undoStack}
              onStartResearch={() => research.start([openFund.id])}
              onClose={() => setOpen(null)}
              onStep={step}
            />
          )
        }
      />

      {adding !== null && (
        <AddToListModal
          fundIds={adding.fundIds}
          title={adding.title}
          suggestedName={adding.suggestedName}
          newOnly={adding.newOnly}
          onClose={() => setAdding(null)}
        />
      )}

      {drawerOpen && (
        <EnrichmentDrawer
          status={enrichment.status}
          research={research}
          researchTargets={researchTargets}
          selectedFundIds={selectedFundIds}
          affinity={affinity}
          dealroom={dealroom}
          onClose={() => setDrawerOpen(false)}
        />
      )}
    </>
  );
}

/**
 * What the pointer is over, said in words. A canvas has no `title`, so every
 * explanation the grid owes a reader comes through here: where a column's
 * values came from, and what a cell is a summary of.
 */
function tipFor(fund: FundSummary, column: FundColumnDef): string | null {
  if (column.id === 'whoKnows' && fund.whoKnows.length > 0) {
    const pairs = whoKnowsPairs(fund);
    // A fund with thirty relationships would otherwise paper over the screen.
    return pairs.length <= TIP_PAIRS_SHOWN
      ? pairs.join('\n')
      : [...pairs.slice(0, TIP_PAIRS_SHOWN), `+${pairs.length - TIP_PAIRS_SHOWN} more`].join('\n');
  }
  if (column.id === 'recentInvestments') {
    // Only worth a hover when it says something the cell does not: who did it.
    return fund.recentInvestments.some((round) => round.partnerName !== null)
      ? recentInvestmentTipLines(fund).join('\n')
      : null;
  }
  if (column.id === 'coInvested') {
    return fund.coInvestments.length === 0
      ? (column.note ?? null)
      : coInvestmentLines(fund).join('\n');
  }
  if (column.id === 'stages') {
    return fund.stages.length === 0
      ? (column.note ?? null)
      : fund.stages.map(stageTitle).join('\n');
  }
  if (column.id === 'regions') return regionsTitle(fund) ?? column.note ?? null;
  if (column.id === 'cheque') return chequeDisplay(fund)?.detail ?? null;
  if (column.id === 'sectors') {
    const observed = unclaimedSectors(fund);
    return observed.length === 0
      ? null
      : `Observed from recent rounds: ${observed.join(', ')}. Not saved.`;
  }
  return null;
}

/**
 * A cell with something waiting on a person. Every kind of cell takes the
 * tint; the two that carry their own display string also take a dot, but only
 * once there is a value to hang it off — an empty cell stays empty rather than
 * reading as a lone, unexplained mark.
 */
function markProposed(cell: GridCell): GridCell {
  const tinted = {
    ...cell,
    themeOverride: { ...cell.themeOverride, bgCell: paletteValue('--attention-bg') },
  };
  if (
    (tinted.kind === GridCellKind.Text || tinted.kind === GridCellKind.Number) &&
    tinted.displayData !== ''
  ) {
    return { ...tinted, displayData: `${tinted.displayData} ●` };
  }
  return tinted;
}

/** One write per fund, so a pasted block reaches the api as one transaction. */
function mergeWrites(
  writes: readonly { fundId: string; write: FundWrite }[],
): { fundId: string; write: FundWrite }[] {
  const merged: { fundId: string; write: FundWrite }[] = [];
  for (const entry of writes) {
    const existing = merged.find(
      (candidate) =>
        candidate.fundId === entry.fundId &&
        candidate.write.kind === 'edits' &&
        entry.write.kind === 'edits',
    );
    if (existing !== undefined && existing.write.kind === 'edits' && entry.write.kind === 'edits') {
      existing.write = { kind: 'edits', edits: [...existing.write.edits, ...entry.write.edits] };
    } else {
      merged.push({ ...entry });
    }
  }
  return merged;
}

/** A new row is a name and nothing else; the rest is filled in from the grid. */
function useCreateFund(): (name: string) => void {
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();
  const create = useMutation(trpc.funds.create.mutationOptions());
  return (name) => {
    create.mutate(
      { name },
      {
        onSuccess: () => {
          invalidateFundRows(queryClient, trpc);
        },
        onError: (error) => toast(error.message),
      },
    );
  };
}
